import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Download, X, Smartphone, Monitor } from "lucide-react";
import { cn } from "@/lib/utils";

export default function InstallPWA() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSGuide, setShowIOSGuide] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches || 
        window.navigator.standalone === true) {
      setIsInstalled(true);
      return;
    }

    // Check if iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(isIOSDevice);

    // Listen for the beforeinstallprompt event
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Check if we've shown the banner before
      const dismissed = localStorage.getItem('pwa-banner-dismissed');
      if (!dismissed) {
        setShowBanner(true);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // Listen for successful install
    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setShowBanner(false);
      setDeferredPrompt(null);
    });

    // Show iOS banner if not dismissed
    if (isIOSDevice) {
      const dismissed = localStorage.getItem('pwa-banner-dismissed');
      if (!dismissed) {
        setTimeout(() => setShowBanner(true), 2000);
      }
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstall = async () => {
    if (isIOS) {
      setShowIOSGuide(true);
      return;
    }

    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setDeferredPrompt(null);
      setShowBanner(false);
    }
  };

  const handleDismiss = () => {
    setShowBanner(false);
    localStorage.setItem('pwa-banner-dismissed', 'true');
  };

  const handleShowBanner = () => {
    localStorage.removeItem('pwa-banner-dismissed');
    setShowBanner(true);
  };

  // Don't show anything if installed
  if (isInstalled) return null;

  // Sidebar button to show install option
  const InstallButton = () => (
    <Button
      data-testid="install-app-btn"
      onClick={isIOS ? () => setShowIOSGuide(true) : (deferredPrompt ? handleInstall : handleShowBanner)}
      variant="ghost"
      className="w-full justify-start gap-3 px-4 py-3 text-slate-300 hover:bg-slate-800 hover:text-white"
    >
      <Download size={20} />
      <span>Installa App</span>
    </Button>
  );

  return (
    <>
      {/* Installation Banner */}
      {showBanner && (
        <div 
          data-testid="install-banner"
          className={cn(
            "fixed bottom-0 left-0 right-0 z-50 p-4",
            "bg-gradient-to-r from-slate-900 to-slate-800",
            "border-t border-slate-700 shadow-2xl",
            "transform transition-transform duration-300 ease-out",
            "lg:bottom-4 lg:left-auto lg:right-4 lg:max-w-sm lg:rounded-lg lg:border"
          )}
        >
          <button
            data-testid="dismiss-banner-btn"
            onClick={handleDismiss}
            className="absolute top-2 right-2 p-1 text-slate-400 hover:text-white"
          >
            <X size={18} />
          </button>
          
          <div className="flex items-start gap-4 pr-6">
            <div className="w-12 h-12 bg-orange-500 rounded-lg flex items-center justify-center flex-shrink-0">
              {isIOS ? <Smartphone className="w-6 h-6 text-white" /> : <Monitor className="w-6 h-6 text-white" />}
            </div>
            
            <div className="flex-1">
              <h3 className="font-heading font-bold text-white text-base">
                Installa AP Depuratore
              </h3>
              <p className="text-sm text-slate-400 mt-1">
                {isIOS 
                  ? "Aggiungi alla schermata Home per un accesso rapido"
                  : "Installa l'app per un accesso rapido e offline"
                }
              </p>
              
              <div className="flex gap-2 mt-3">
                <Button
                  data-testid="install-now-btn"
                  onClick={handleInstall}
                  className="bg-orange-500 hover:bg-orange-600 text-white text-sm px-4 py-2"
                >
                  <Download className="w-4 h-4 mr-2" />
                  {isIOS ? "Come fare" : "Installa"}
                </Button>
                <Button
                  variant="ghost"
                  onClick={handleDismiss}
                  className="text-slate-400 hover:text-white text-sm"
                >
                  Non ora
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* iOS Installation Guide Modal */}
      {showIOSGuide && (
        <div 
          data-testid="ios-guide-modal"
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60"
          onClick={() => setShowIOSGuide(false)}
        >
          <div 
            className="bg-white rounded-lg shadow-2xl max-w-sm w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-lg text-slate-900">
                Installa su iPhone/iPad
              </h3>
              <button
                onClick={() => setShowIOSGuide(false)}
                className="p-1 text-slate-400 hover:text-slate-600"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                  <span className="font-bold text-orange-600">1</span>
                </div>
                <p className="text-slate-600">
                  Tocca il pulsante <strong>Condividi</strong> in basso (l'icona con la freccia verso l'alto)
                </p>
              </div>
              
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                  <span className="font-bold text-orange-600">2</span>
                </div>
                <p className="text-slate-600">
                  Scorri e seleziona <strong>"Aggiungi alla schermata Home"</strong>
                </p>
              </div>
              
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                  <span className="font-bold text-orange-600">3</span>
                </div>
                <p className="text-slate-600">
                  Conferma toccando <strong>"Aggiungi"</strong> in alto a destra
                </p>
              </div>
            </div>
            
            <Button
              onClick={() => setShowIOSGuide(false)}
              className="w-full mt-6 bg-slate-900 hover:bg-slate-800 text-white"
            >
              Ho capito
            </Button>
          </div>
        </div>
      )}

      {/* Export the button for sidebar */}
      <InstallButton />
    </>
  );
}

// Export a standalone button for use in sidebar
export function InstallAppButton() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSGuide, setShowIOSGuide] = useState(false);

  useEffect(() => {
    if (window.matchMedia('(display-mode: standalone)').matches || 
        window.navigator.standalone === true) {
      setIsInstalled(true);
      return;
    }

    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(isIOSDevice);

    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstall = async () => {
    if (isIOS) {
      setShowIOSGuide(true);
      return;
    }

    if (!deferredPrompt) {
      // Try to show banner anyway
      localStorage.removeItem('pwa-banner-dismissed');
      window.location.reload();
      return;
    }

    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
  };

  if (isInstalled) return null;

  return (
    <>
      <button
        data-testid="sidebar-install-btn"
        onClick={handleInstall}
        className="flex items-center gap-3 w-full px-4 py-3 rounded-sm transition-all font-medium text-sm text-slate-300 hover:bg-slate-800 hover:text-white"
      >
        <Download size={20} />
        <span>Installa App</span>
      </button>

      {/* iOS Guide Modal */}
      {showIOSGuide && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60"
          onClick={() => setShowIOSGuide(false)}
        >
          <div 
            className="bg-white rounded-lg shadow-2xl max-w-sm w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-lg text-slate-900">
                Installa su iPhone/iPad
              </h3>
              <button
                onClick={() => setShowIOSGuide(false)}
                className="p-1 text-slate-400 hover:text-slate-600"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                  <span className="font-bold text-orange-600">1</span>
                </div>
                <p className="text-slate-600">
                  Tocca il pulsante <strong>Condividi</strong> in basso (l'icona con la freccia verso l'alto)
                </p>
              </div>
              
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                  <span className="font-bold text-orange-600">2</span>
                </div>
                <p className="text-slate-600">
                  Scorri e seleziona <strong>"Aggiungi alla schermata Home"</strong>
                </p>
              </div>
              
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                  <span className="font-bold text-orange-600">3</span>
                </div>
                <p className="text-slate-600">
                  Conferma toccando <strong>"Aggiungi"</strong> in alto a destra
                </p>
              </div>
            </div>
            
            <Button
              onClick={() => setShowIOSGuide(false)}
              className="w-full mt-6 bg-slate-900 hover:bg-slate-800 text-white"
            >
              Ho capito
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
